import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  CircularProgress,
  Badge,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchCategories } from "../../api/catalogApi";
import { getCurrentPath } from "../../utils/authRedirect";

import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HeadsetMicOutlinedIcon from "@mui/icons-material/HeadsetMicOutlined";

import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import { useAuth } from "../../services/auth/AuthContext";

function normalizeCategories(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.results)) return raw.results;
  if (Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw.categories)) return raw.categories;
  if (Array.isArray(raw.meta?.categories)) return raw.meta.categories;
  return [];
}

function getChildren(cat) {
  if (Array.isArray(cat?.subcategories) && cat.subcategories.length > 0)
    return cat.subcategories;
  if (Array.isArray(cat?.children) && cat.children.length > 0)
    return cat.children;
  return [];
}

function getTitle(cat) {
  return cat?.title || cat?.name || cat?.slug || "بدون عنوان";
}

const DrawerCategoryItem = ({ cat, level = 0, onSelectCategory, onClose }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const children = getChildren(cat);
  const hasChildren = children.length > 0;

  const handleSelect = (e) => {
    if (e) e.stopPropagation();

    if (onSelectCategory) {
      onSelectCategory(cat.slug);
    } else {
      navigate(`/category/${cat.slug}`);
    }

    if (onClose) onClose();
  };

  const handleToggle = (e) => {
    if (e) e.stopPropagation();
    setOpen((prev) => !prev);
  };

  return (
    <>
      <ListItemButton
        onClick={hasChildren ? handleToggle : handleSelect}
        sx={{
          py: 1,
          pl: 1.5,
          pr: 1.5 + level * 1.8,
          borderRadius: 2,
          mb: 0.5,
          flexDirection: "row",
          justifyContent: "space-between",
          textAlign: "right",
          bgcolor: level > 0 ? "grey.50" : "transparent",
          "&:hover": { bgcolor: "grey.100" },
        }}
      >
        {/* Title click always navigates to /category/:slug */}
        <Box
          onClick={handleSelect}
          sx={{ flex: 1, minWidth: 0, cursor: "pointer", textAlign: "right" }}
        >
          <ListItemText
            primary={getTitle(cat)}
            primaryTypographyProps={{
              fontSize: level > 0 ? "0.83rem" : "0.88rem",
              fontWeight: level > 0 ? 400 : 500,
              color: "text.primary",
              textAlign: "right",
            }}
          />
        </Box>
        {hasChildren && (
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{ p: 0.3, ml: 1, color: "grey.600" }}
            aria-label={open ? "بستن زیرمجموعه" : "باز کردن زیرمجموعه"}
          >
            {open ? (
              <KeyboardArrowDownIcon fontSize="small" />
            ) : (
              <KeyboardArrowLeftIcon fontSize="small" />
            )}
          </IconButton>
        )}
      </ListItemButton>

      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box
            sx={{
              borderRight: "2px solid",
              borderColor: "grey.100",
              mr: 2.2,
              pr: 0.5,
            }}
          >
            <List component="div" disablePadding sx={{ p: 0, m: 0 }}>
              {children.map((sub) => (
                <DrawerCategoryItem
                  key={sub.slug || sub.id || sub.title}
                  cat={sub}
                  level={level + 1}
                  onSelectCategory={onSelectCategory}
                  onClose={onClose}
                />
              ))}
            </List>
          </Box>
        </Collapse>
      )}
    </>
  );
};

export default function MenuDrawer({
  open,
  onClose,
  categories = [],
  onSelectCategory,
  handleNavigation,
  cartCount = 0,
}) {
  const { isAuthenticated, logout } = useAuth();

  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [fetched, setFetched] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const propCategories = useMemo(
    () => normalizeCategories(categories),
    [categories],
  );

  // Self-fetch fallback: if parent passes nothing, fetch directly
  // so the drawer never stays empty because of prop drilling issues.
  useEffect(() => {
    if (!open) return;
    if (propCategories.length > 0) return;

    let cancelled = false;
    const load = async () => {
      setLoadingCats(true);
      try {
        const data = await fetchCategories();
        if (!cancelled) setFetched(normalizeCategories(data));
      } catch (err) {
        console.error("MenuDrawer category fetch failed", err);
        if (!cancelled) setFetched([]);
      } finally {
        if (!cancelled) setLoadingCats(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, propCategories.length]);

  const visibleCategories =
    propCategories.length > 0 ? propCategories : fetched;

  const handleSelectCategory = (slug) => {
    if (!slug) return;
    if (onSelectCategory) {
      onSelectCategory(slug);
    } else {
      navigate(`/category/${slug}`);
    }
    
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    
    if (onClose) onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "85vw", sm: 360 },
          maxWidth: "100%",
          height: "100dvh",
          boxSizing: "border-box",
          direction: "rtl",
          p: 2,
          bgcolor: "#ffffff",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1.5,
          mb: 1.5,
          borderBottom: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Typography variant="h6" sx={{ fontSize: "1.05rem", fontWeight: 700 }}>
          منوی دسترسی
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "grey.600" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", pr: 0.5 }}>
        <List disablePadding>
          <ListItemButton
            onClick={() => setCategoriesOpen(!categoriesOpen)}
            sx={{
              py: 1.2,
              px: 1.5,
              borderRadius: 2,
              mb: 0.5,
              justifyContent: "space-between",
              textAlign: "right",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                textAlign: "right",
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: "#ef4056" }}>
                <CategoryOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary="دسته‌بندی محصولات"
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  color: "#ef4056",
                  textAlign: "right",
                }}
              />
            </Box>
            {categoriesOpen ? (
              <KeyboardArrowDownIcon
                fontSize="small"
                sx={{ color: "#ef4056" }}
              />
            ) : (
              <KeyboardArrowLeftIcon
                fontSize="small"
                sx={{ color: "grey.600" }}
              />
            )}
          </ListItemButton>

          <Collapse in={categoriesOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ p: 0, m: 0 }}>
              {loadingCats ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 2,
                  }}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    در حال بارگذاری دسته‌بندی‌ها...
                  </Typography>
                </Box>
              ) : visibleCategories.length > 0 ? (
                visibleCategories.map((cat) => (
                  <DrawerCategoryItem
                    key={cat.slug || cat.id || cat.title}
                    cat={cat}
                    onSelectCategory={handleSelectCategory}
                    onClose={onClose}
                  />
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 1.5, py: 1, textAlign: "right" }}
                >
                  دسته‌بندی یافت نشد.
                </Typography>
              )}
            </List>
          </Collapse>
        </List>

        {/* Quick links: below categories, right-aligned like categories */}
        <List disablePadding sx={{ mt: 1, pt: 1 }}>
          <ListItemButton
            onClick={() => {
              if (onClose) onClose();
              navigate("/");
            }}
            sx={{
              py: 1.2,
              px: 1.5,
              borderRadius: 2,
              mb: 0.5,
              justifyContent: "space-between",
              textAlign: "right",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                textAlign: "right",
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: "grey.600" }}>
                <HomeOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary="خانه"
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  textAlign: "right",
                }}
              />
            </Box>
          </ListItemButton>

          {/* Support/Contact  */}
          <ListItemButton
            onClick={() => {
              if (onClose) onClose();

              const waShortLink = "https://wa.me/message/LNAEOJV5FWMQI1";
              window.open(waShortLink, "_blank", "noopener,noreferrer");
            }}
            sx={{
              py: 1.2,
              px: 1.5,
              borderRadius: 2,
              mb: 0.5,
              justifyContent: "space-between",
              textAlign: "right",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: 1,
                textAlign: "right",
              }}
            >
              <ListItemIcon sx={{ minWidth: 36, color: "grey.600" }}>
                <Badge
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: "#ef4056",
                      color: "#fff",
                      fontSize: "0.65rem",
                      height: 16,
                      minWidth: 16,
                    },
                  }}
                >
                  <HeadsetMicOutlinedIcon />
                </Badge>
              </ListItemIcon>
              {/* Contact Us  */}
              <ListItemText
                primary="تماس با ما"
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  textAlign: "right",
                }}
              />
            </Box>
          </ListItemButton>

          {isAuthenticated && (
            <>
              {/* WishList  */}
              <ListItemButton
                onClick={() => {
                  if (onClose) onClose();
                  navigate("/wishlist");
                }}
                sx={{
                  py: 1.2,
                  px: 1.5,
                  borderRadius: 2,
                  justifyContent: "space-between",
                  textAlign: "right",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "grey.600" }}>
                    <FavoriteBorderIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="علاقه مندی ها"
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      textAlign: "right",
                    }}
                  />
                </Box>
              </ListItemButton>

              {/* Invoice  */}
              <ListItemButton
                onClick={() => {
                  if (onClose) onClose();
                  navigate("/invoices");
                }}
                sx={{
                  py: 1.2,
                  px: 1.5,
                  borderRadius: 2,
                  justifyContent: "space-between",
                  textAlign: "right",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "grey.600" }}>
                    <ShoppingBagOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="سفارشات من"
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      textAlign: "right",
                    }}
                  />
                </Box>
              </ListItemButton>

              {/* Cart  */}
              {/* <ListItemButton
                onClick={() => {
                  if (onClose) onClose();
                  navigate("/cart");
                }}
                sx={{
                  py: 1.2,
                  px: 1.5,
                  borderRadius: 2,
                  mb: 0.5,
                  justifyContent: "space-between",
                  textAlign: "right",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "grey.600" }}>
                    <Badge
                      badgeContent={cartCount}
                      max={99}
                      sx={{
                        "& .MuiBadge-badge": {
                          bgcolor: "#ef4056",
                          color: "#fff",
                          fontSize: "0.65rem",
                          height: 16,
                          minWidth: 16,
                        },
                      }}
                    >
                      <ShoppingCartOutlinedIcon />
                    </Badge>
                  </ListItemIcon>

                  <ListItemText
                    primary="سبد خرید"
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      textAlign: "right",
                    }}
                  />
                </Box>
              </ListItemButton> */}

              {/* FAQ  */}
              <ListItemButton
                onClick={() => {
                  if (onClose) onClose();
                  navigate("/faq");
                }}
                sx={{
                  py: 1.2,
                  px: 1.5,
                  borderRadius: 2,
                  mb: 0.5,
                  justifyContent: "space-between",
                  textAlign: "right",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "grey.600" }}>
                    <Badge
                      sx={{
                        "& .MuiBadge-badge": {
                          bgcolor: "#ef4056",
                          color: "#fff",
                          fontSize: "0.65rem",
                          height: 16,
                          minWidth: 16,
                        },
                      }}
                    >
                      <HelpOutlineIcon />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary="سوالات پر تکرار"
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      textAlign: "right",
                    }}
                  />
                </Box>
              </ListItemButton>

              {/* User Profile  */}
              <ListItemButton
                onClick={() => {
                  if (onClose) onClose();
                  navigate("/userProfile");
                }}
                sx={{
                  py: 1.2,
                  px: 1.5,
                  borderRadius: 2,
                  mb: 0.5,
                  justifyContent: "space-between",
                  textAlign: "right",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                    textAlign: "right",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36, color: "grey.600" }}>
                    <Badge
                      sx={{
                        "& .MuiBadge-badge": {
                          bgcolor: "#ef4056",
                          color: "#fff",
                          fontSize: "0.65rem",
                          height: 16,
                          minWidth: 16,
                        },
                      }}
                    >
                      <PersonOutlineIcon />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary="حساب کاربری"
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      textAlign: "right",
                    }}
                  />
                </Box>
              </ListItemButton>
            </>
          )}
        </List>
      </Box>

      {/* FIXED BOTTOM SECTION */}
      <Box
        sx={{
          pt: 1.5,
          pb: "calc(12px + env(safe-area-inset-bottom, 12px))",
          mt: "auto",
          borderTop: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <List disablePadding>
          {isAuthenticated ? (
            <ListItemButton
              onClick={async () => {
                if (onClose) onClose();
                await logout();
              }}
              sx={{
                py: 1.2,
                px: 1.5,
                borderRadius: 2,
                justifyContent: "space-between",
                textAlign: "right",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  textAlign: "right",
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "error.main" }}>
                  <LogoutOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  primary="خروج از حساب"
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    textAlign: "right",
                    color: "error.main",
                  }}
                />
              </Box>
            </ListItemButton>
          ) : (
            <ListItemButton
              onClick={() => {
                if (onClose) onClose();
                navigate("/login", {
                  state: { from: getCurrentPath(location) },
                });
              }}
              sx={{
                py: 1.2,
                px: 1.5,
                borderRadius: 2,
                justifyContent: "space-between",
                textAlign: "right",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  textAlign: "right",
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: "grey.600" }}>
                  <LoginOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  primary="ورود / ثبت‌نام"
                  primaryTypographyProps={{
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    textAlign: "right",
                  }}
                />
              </Box>
            </ListItemButton>
          )}
        </List>
      </Box>
    </Drawer>
  );
}
